"use client";

import { c as _c } from "react/compiler-runtime";
import { UserFollowBtnSize } from "@/components/user/utils/UserFollowBtn";
import type { DropInteractionParams } from "@/components/waves/drops/Drop";
import { useEmoji } from "@/contexts/EmojiContext";
import { ApiNotificationCause } from "@/generated/models/ApiNotificationCause";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { INotificationDropBoosted, INotificationDropReacted, INotificationDropVoted } from "@/types/feed.types";
import ReactionEmojiPreview from "./ReactionEmojiPreview";
import NotificationsFollowBtn from "../NotificationsFollowBtn";
import NotificationDrop from "../subcomponents/NotificationDrop";
import NotificationHeader from "../subcomponents/NotificationHeader";
import NotificationTimestamp from "../subcomponents/NotificationTimestamp";
import { getIsDirectMessage, useWaveNavigation } from "../utils/navigationUtils";
import { formatSignedNotificationNumber, getNotificationRatingColor, isNotificationNumber } from "../utils/notificationRatingUtils";
export const getNotificationVoteColor = (vote: number) => {
  const color = getNotificationRatingColor(vote);
  return color === "tw-text-iron-400" ? "tw-text-iron-500" : color;
};
type NotificationUnion = INotificationDropVoted | INotificationDropReacted | INotificationDropBoosted;
interface Props {
  readonly notification: NotificationUnion;
  readonly activeDrop: ActiveDropState | null;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
}
function NotificationVoteInlinePart(t0) {
  const $ = _c(17);
  const {
    label,
    value,
    separator: t1,
    textClassName: t2
  } = t0;
  const separator = t1 === undefined ? "bullet" : t1;
  const textClassName = t2 === undefined ? "tw-text-sm" : t2;
  if (!isNotificationNumber(value)) {
    return null;
  }
  const t3 = separator === "arrow" ? `${textClassName} tw-font-bold tw-text-iron-400` : "tw-text-xs tw-font-bold tw-text-iron-400";
  const t4 = separator === "arrow" ? "\u2192" : "\u2022";
  let t5;
  if ($[0] !== t3 || $[1] !== t4) {
    t5 = <span className={t3}>{t4}</span>;
    $[0] = t3;
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const t6 = `${textClassName} tw-font-normal tw-text-iron-400`;
  let t7;
  if ($[3] !== label || $[4] !== t6) {
    t7 = <span className={t6}>{label}:</span>;
    $[3] = label;
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== value) {
    t8 = getNotificationVoteColor(value);
    $[6] = value;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const t9 = `${t8} ${textClassName} tw-font-medium tw-tabular-nums`;
  let t10;
  if ($[8] !== value) {
    t10 = formatSignedNotificationNumber(value);
    $[8] = value;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== t10 || $[11] !== t9) {
    t11 = <span className={t9}>{t10}</span>;
    $[10] = t10;
    $[11] = t9;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== t11 || $[14] !== t5 || $[15] !== t7) {
    t12 = <span className="tw-inline-flex tw-items-center tw-gap-x-1 tw-whitespace-nowrap">{t5}{t7}{t11}</span>;
    $[13] = t11;
    $[14] = t5;
    $[15] = t7;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  return t12;
}
export default function NotificationDropReacted(t0) {
  const $ = _c(65);
  const {
    notification,
    activeDrop,
    onReply,
    onDropContentClick
  } = t0;
  const {
    createReplyClickHandler,
    createQuoteClickHandler
  } = useWaveNavigation();
  const {
    findCustomEmoji,
    findNativeEmoji
  } = useEmoji();
  const isVoted = notification.cause === ApiNotificationCause.DropVoted;
  const isReacted = notification.cause === ApiNotificationCause.DropReacted;
  const isBoosted = notification.cause === ApiNotificationCause.DropBoosted;
  const drop = notification.related_drops[0];
  if (!drop) {
    return null;
  }
  let actionElement;
  if (isVoted) {
    const voteValue = notification.additional_context.vote;
    const voteChange = notification.additional_context.vote_change;
    const totalVote = notification.additional_context.total_vote;
    let t1;
    if ($[0] !== voteChange) {
      t1 = isNotificationNumber(voteChange);
      $[0] = voteChange;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    const hasVoteChange = t1;
    const isInitialRating = !hasVoteChange || voteChange === voteValue;
    let t2;
    if ($[2] !== isInitialRating || $[3] !== voteChange || $[4] !== voteValue) {
      t2 = isInitialRating ? <><span className="tw-text-base tw-font-normal tw-text-iron-400">rated</span><span className={`${getNotificationVoteColor(voteValue)} ${"tw-text-base"} tw-font-medium tw-tabular-nums`}>{formatSignedNotificationNumber(voteValue)}</span></> : <><span className="tw-text-base tw-font-normal tw-text-iron-400">updated rating by</span><span className={`${getNotificationVoteColor(voteChange)} ${"tw-text-base"} tw-font-medium tw-tabular-nums`}>{formatSignedNotificationNumber(voteChange)}</span><NotificationVoteInlinePart label="New rating" value={voteValue} separator="arrow" textClassName="tw-text-base" /></>;
      $[2] = isInitialRating;
      $[3] = voteChange;
      $[4] = voteValue;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    let t3;
    if ($[6] !== totalVote) {
      t3 = <NotificationVoteInlinePart label="New Total" value={totalVote} textClassName="tw-text-base" />;
      $[6] = totalVote;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    let t4;
    if ($[8] !== notification.created_at) {
      t4 = <NotificationTimestamp createdAt={notification.created_at} className="tw-text-base" />;
      $[8] = notification.created_at;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    let t5;
    if ($[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
      t5 = <>{t2}{t3}{t4}</>;
      $[10] = t2;
      $[11] = t3;
      $[12] = t4;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    actionElement = t5;
  } else {
    if (isBoosted) {
      let t1;
      if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = <span className="tw-text-sm tw-font-normal tw-text-iron-400">🔥 boosted 🔥</span>;
        $[14] = t1;
      } else {
        t1 = $[14];
      }
      let t2;
      if ($[15] !== notification.created_at) {
        t2 = <>{t1}<NotificationTimestamp createdAt={notification.created_at} /></>;
        $[15] = notification.created_at;
        $[16] = t2;
      } else {
        t2 = $[16];
      }
      actionElement = t2;
    } else {
      if (isReacted) {
        let rawId;
        let t1;
        if ($[17] !== findCustomEmoji || $[18] !== findNativeEmoji || $[19] !== notification.additional_context.reaction) {
          rawId = notification.additional_context.reaction.replaceAll(":", "");
          t1 = !findCustomEmoji(rawId) && !findNativeEmoji(rawId);
          $[17] = findCustomEmoji;
          $[18] = findNativeEmoji;
          $[19] = notification.additional_context.reaction;
          $[20] = rawId;
          $[21] = t1;
        } else {
          rawId = $[20];
          t1 = $[21];
        }
        if (t1) {
          return null;
        }
        let t2;
        if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
          t2 = <span className="tw-text-sm tw-font-normal tw-text-iron-400">reacted</span>;
          $[22] = t2;
        } else {
          t2 = $[22];
        }
        let t3;
        if ($[23] !== rawId) {
          t3 = <ReactionEmojiPreview rawId={rawId} />;
          $[23] = rawId;
          $[24] = t3;
        } else {
          t3 = $[24];
        }
        let t4;
        if ($[25] !== notification.created_at) {
          t4 = <NotificationTimestamp createdAt={notification.created_at} />;
          $[25] = notification.created_at;
          $[26] = t4;
        } else {
          t4 = $[26];
        }
        let t5;
        if ($[27] !== t3 || $[28] !== t4) {
          t5 = <>{t2}{t3}{t4}</>;
          $[27] = t3;
          $[28] = t4;
          $[29] = t5;
        } else {
          t5 = $[29];
        }
        actionElement = t5;
      } else {
        return null;
      }
    }
  }
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[30] !== actionElement || $[31] !== activeDrop || $[32] !== createQuoteClickHandler || $[33] !== createReplyClickHandler || $[34] !== drop || $[35] !== isVoted || $[36] !== notification.related_identity || $[37] !== onReply) {
    const isDirectMessage = getIsDirectMessage(drop.wave);
    t6 = "tw-flex tw-w-full tw-flex-col tw-space-y-2";
    const t8 = isVoted ? "tw-text-base" : undefined;
    let t9;
    if ($[46] !== notification.related_identity) {
      t9 = <NotificationsFollowBtn profile={notification.related_identity} size={UserFollowBtnSize.SMALL} />;
      $[46] = notification.related_identity;
      $[47] = t9;
    } else {
      t9 = $[47];
    }
    if ($[48] !== actionElement || $[49] !== notification.related_identity || $[50] !== t8 || $[51] !== t9) {
      t7 = <NotificationHeader author={notification.related_identity} authorClassName={t8} actions={t9}>{actionElement}</NotificationHeader>;
      $[48] = actionElement;
      $[49] = notification.related_identity;
      $[50] = t8;
      $[51] = t9;
      $[52] = t7;
    } else {
      t7 = $[52];
    }
    T0 = NotificationDrop;
    t1 = drop;
    t2 = activeDrop;
    t3 = onReply;
    t4 = createReplyClickHandler(drop.wave.id, isDirectMessage);
    t5 = createQuoteClickHandler(isDirectMessage);
    $[30] = actionElement;
    $[31] = activeDrop;
    $[32] = createQuoteClickHandler;
    $[33] = createReplyClickHandler;
    $[34] = drop;
    $[35] = isVoted;
    $[36] = notification.related_identity;
    $[37] = onReply;
    $[38] = T0;
    $[39] = t1;
    $[40] = t2;
    $[41] = t3;
    $[42] = t4;
    $[43] = t5;
    $[44] = t6;
    $[45] = t7;
  } else {
    T0 = $[38];
    t1 = $[39];
    t2 = $[40];
    t3 = $[41];
    t4 = $[42];
    t5 = $[43];
    t6 = $[44];
    t7 = $[45];
  }
  let t8;
  if ($[53] !== T0 || $[54] !== onDropContentClick || $[55] !== t1 || $[56] !== t2 || $[57] !== t3 || $[58] !== t4 || $[59] !== t5) {
    t8 = <T0 drop={t1} activeDrop={t2} onReply={t3} onReplyClick={t4} onQuoteClick={t5} onDropContentClick={onDropContentClick} />;
    $[53] = T0;
    $[54] = onDropContentClick;
    $[55] = t1;
    $[56] = t2;
    $[57] = t3;
    $[58] = t4;
    $[59] = t5;
    $[60] = t8;
  } else {
    t8 = $[60];
  }
  let t9;
  if ($[61] !== t6 || $[62] !== t7 || $[63] !== t8) {
    t9 = <div className={t6}>{t7}{t8}</div>;
    $[61] = t6;
    $[62] = t7;
    $[63] = t8;
    $[64] = t9;
  } else {
    t9 = $[64];
  }
  return t9;
}